import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "core-js/modules/es.array.concat.js";
import "core-js/modules/es.array.find.js";
import "core-js/modules/es.array.find-index.js";
import "core-js/modules/es.array.slice.js";
import "core-js/modules/es.function.name.js";
import "core-js/modules/es.object.to-string.js";
import React, { createContext, useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import { KEY } from 'utils/ui/constants';
import useEventListener from 'hooks/useEventListener';
import { hideForModal } from './accessiblyHideModalBackground';
import { jsx as _jsx } from "react/jsx-runtime";
var noop = function noop() {
  return undefined;
};
var defaultState = {
  register: noop,
  unregister: noop
};

// Modal Manager

var ModalContext = /*#__PURE__*/createContext(defaultState);
var ModalStackContext = /*#__PURE__*/createContext([]);
function ModalManager(_ref) {
  var children = _ref.children;
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    modalStack = _useState2[0],
    setModalStack = _useState2[1];
  var elementToFocus = useRef(null);

  /**
   * Add a new modal to the stack with a name, focusAnchorRef (the
   * element that triggered its opening), ref of the modal component,
   * and an onCloseRef handler ref that will close the modal.
   * ModalManager does not _own_ the open/close state of a modal,
   * it just needs to track it in order to handle focus restoration.
   */

  var register = useCallback(function (newModalObject) {
    setModalStack(function (prevModalStack) {
      var isModalAlreadyRegistered = Boolean(prevModalStack.find(function (modal) {
        return modal.name === newModalObject.name;
      }));
      if (isModalAlreadyRegistered) {
        return prevModalStack;
      }
      return [].concat(_toConsumableArray(prevModalStack), [newModalObject]);
    });
  }, []);
  var unregister = useCallback(function (_ref2) {
    var name = _ref2.name;
    if (!name) {
      return;
    }
    setModalStack(function (prevModalStack) {
      var indexOfModalToClose = prevModalStack.findIndex(function (modal) {
        return modal.name === name;
      });
      if (prevModalStack.length === 0 || indexOfModalToClose === -1) {
        return prevModalStack;
      }
      var focusAnchorRef = prevModalStack[indexOfModalToClose].focusAnchorRef;
      elementToFocus.current = focusAnchorRef.current;
      return prevModalStack.slice(0, indexOfModalToClose);
    });
  }, []);

  // Focus the element that originally opened the modal
  // There should only be one after a modal was un-registered)
  useEffect(function () {
    // The timeout is needed to allow the focus lock script
    // (react-focus-lock) to unlock the focus target
    setTimeout(function () {
      if (elementToFocus.current) {
        elementToFocus.current.focus();
        elementToFocus.current = null;
      }
    }, 0);
  }, [modalStack]);

  // Apply aria-hidden to everything but the top-most modal.
  useEffect(function () {
    if (modalStack.length) {
      var topMostModal = modalStack[modalStack.length - 1];
      var undoAriaHideOthers = hideForModal(topMostModal === null || topMostModal === void 0 ? void 0 : topMostModal.ref.current);
      return function cleanUp() {
        undoAriaHideOthers();
      };
    }
  }, [modalStack]);

  // Close the topmost modal when the Esc key is pressed
  useEventListener('keydown', function (event) {
    if (event.key === KEY.ESC) {
      var _topMostModal$onClose, _topMostModal$onClose2;
      var topMostModal = modalStack[modalStack.length - 1];
      (_topMostModal$onClose = (_topMostModal$onClose2 = topMostModal.onCloseRef).current) === null || _topMostModal$onClose === void 0 || _topMostModal$onClose.call(_topMostModal$onClose2);
    }
  }, {
    isEnabled: modalStack.length > 0
  });
  var modalContext = useMemo(function () {
    return {
      register: register,
      unregister: unregister
    };
  }, [register, unregister]);
  return /*#__PURE__*/_jsx(ModalContext.Provider, {
    value: modalContext,
    children: /*#__PURE__*/_jsx(ModalStackContext.Provider, {
      value: modalStack,
      children: children
    })
  });
}

// useModalManager

function useModalManager(_ref3) {
  var _modalStack;
  var name = _ref3.name,
    onRequestClose = _ref3.onRequestClose,
    focusAnchor = _ref3.focusAnchor;
  var modalRef = useRef(null);
  var onCloseRef = useRef(onRequestClose);
  var focusAnchorRef = useRef(focusAnchor);
  var _useContext = useContext(ModalContext),
    register = _useContext.register,
    unregister = _useContext.unregister;
  var modalStack = useContext(ModalStackContext);
  var isAtTop = ((_modalStack = modalStack[modalStack.length - 1]) === null || _modalStack === void 0 ? void 0 : _modalStack.name) === name;
  useEffect(function () {
    // Maintain up-to-date refs of the onRequestClose
    // and focusAnchor props, as we don't want them to
    // trigger the register/unregister effect.
    // Simply removing them from that effect's dependencies
    // would cause them to potentially go stale.
    onCloseRef.current = onRequestClose;
    focusAnchorRef.current = focusAnchor || document.activeElement || document.body;
  });
  useEffect(function () {
    register({
      name: name,
      onCloseRef: onCloseRef,
      focusAnchorRef: focusAnchorRef,
      ref: modalRef
    });
    return function () {
      unregister({
        name: name
      });
    };
  }, [name, register, unregister]);
  return {
    modalRef: modalRef,
    isAtTop: isAtTop,
    modalCount: modalStack.length
  };
}
export { ModalManager, useModalManager, ModalContext, ModalStackContext };